// externals
import { useDispatch } from 'react-redux';

// ui components
import { Modal } from '@interstate/components/Modal';
import { Grid } from '@interstate/components/Grid';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';

// store
import * as pushToFIActionCreators from '@makemydeal/dr-dash-store/actions/pushToFIActionCreators';

// types
import type { PushToFIProvider } from '@makemydeal/dr-dash-types';

// hooks
import { useValidationGuard } from '../../hooks/useValidationGuard';

// styles (reuse RouteOne footer styling)
import { StyledPushToRouteOneFooter } from '../pushToRouteOne/PushToRouteOneDialog.style';

export interface IPushToFIDialogProps {
    provider: PushToFIProvider;
    show: boolean;
    onHide(): void;
}

const PushToFIDialog = (props: IPushToFIDialogProps) => {
    const { provider, show, onHide } = props;
    const dispatch = useDispatch();
    const guard = useValidationGuard();

    const prefix = `push-to-fi-dialog-${provider.targetPlatformId.toLowerCase()}`;

    const header = (
        <Typography variant="h3" sx={{ paddingTop: '1rem' }} data-testid={`${prefix}-header`}>
            {provider.modalCopy.title}
        </Typography>
    );

    const footer = (
        <StyledPushToRouteOneFooter>
            <Grid container justifyContent="flex-end" data-testid={`${prefix}-footer-cancel`}>
                <Button data-testid={`${prefix}-cancel-button`} buttonStyle="secondary" onClick={onHide}>
                    Cancel
                </Button>
            </Grid>
            <Grid container justifyContent="flex-end" data-testid={`${prefix}-footer-confirm`}>
                <Button
                    data-testid={`${prefix}-confirm-button`}
                    buttonStyle="primary"
                    onClick={guard.wrap(() => {
                        dispatch(pushToFIActionCreators.confirmPushToFI(provider.targetPlatformId));
                        onHide();
                    })}
                >
                    Confirm
                </Button>
            </Grid>
        </StyledPushToRouteOneFooter>
    );

    return (
        <Modal
            size="large"
            id={prefix}
            data-testid={prefix}
            show={show}
            header={header}
            footer={{ footerComponent: footer }}
            onHide={onHide}
        >
            <Typography data-testid={`${prefix}-confirm-message`} variant="body-sm" tag="p" color="sem.color.on-surface.primary">
                {provider.modalCopy.confirmMessage}
            </Typography>
        </Modal>
    );
};

export default PushToFIDialog;
